<template>
  <div class="color-picker-item">
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      location="start"
    >
      <template #activator="{ props: menuProps }">
        <button
          v-bind="menuProps"
          class="color-picker-item__swatch"
          :style="{ backgroundColor: value }"
          :aria-label="`Edit ${label} color`"
          type="button"
        />
      </template>
      <v-color-picker
        :model-value="value"
        mode="hex"
        show-swatches
        @update:model-value="handleColorChange"
      />
    </v-menu>
    <span class="color-picker-item__label">{{ label }}</span>
    <span class="color-picker-item__value">{{ value }}</span>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

interface Props {
  label: string;
  colorKey: string;
  value: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  update: [key: string, value: string];
}>();

const menuOpen = ref(false);

function handleColorChange(newColor: string | null) {
  if (newColor) {
    emit("update", props.colorKey, newColor);
  }
}
</script>

<style scoped lang="scss">
.color-picker-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  &__swatch {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    padding: 0;
    background: none;

    &:hover {
      transform: scale(1.1);
      border-color: rgba(255, 255, 255, 0.3);
    }

    &:focus {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 2px;
    }
  }

  &__label {
    flex: 1;
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 0.6875rem;
    font-family: monospace;
    color: rgb(var(--v-theme-outline));
    text-transform: uppercase;
  }
}

// Light theme
.v-theme--light {
  .color-picker-item {
    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &__swatch {
      border-color: rgba(0, 0, 0, 0.1);

      &:hover {
        border-color: rgba(0, 0, 0, 0.3);
      }
    }
  }
}
</style>
